import React, { useState, useEffect } from 'react';
import axios from 'axios';

const CollegeManagement = () => {
  // State for colleges data
  const [colleges, setColleges] = useState([]);
  const [filteredColleges, setFilteredColleges] = useState([]);
  
  // Form state
  const [formData, setFormData] = useState({
    college_name: '',
    college_code: '',
    programme: '',
    state: '',
    branch: [],
    is_disabled: false
  });
  const [currentBranch, setCurrentBranch] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [currentCollegeId, setCurrentCollegeId] = useState(null);
  
  // UI state
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [collegeToDelete, setCollegeToDelete] = useState(null);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState('');
  const itemsPerPage = 5;

  // API base URL
  const API_BASE_URL = 'http://localhost:5100/api/v2/college';

  // Fetch colleges from API
  const fetchColleges = async () => {
    setIsLoading(true);
    setApiError('');
    try {
      const response = await axios.get(`${API_BASE_URL}/all`);
      console.log('Colleges fetched:', response.data.colleges);
      setColleges(response.data.colleges);
      setFilteredColleges(response.data.colleges);
    } catch (error) {
      console.error('Error fetching colleges:', error);
      setApiError('Failed to fetch colleges. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Load data on component mount
  useEffect(() => {
    fetchColleges();
  }, []);

  // Filter colleges based on search term
  useEffect(() => {
    const filtered = colleges.filter(college => {
      const searchLower = searchTerm.toLowerCase();
      return (
        college.college_name.toLowerCase().includes(searchLower) ||
        college.college_code.toLowerCase().includes(searchLower) ||
        (college.programme && college.programme.toLowerCase().includes(searchLower)) ||
        (college.state && college.state.toLowerCase().includes(searchLower))
      );
    });
    setFilteredColleges(filtered);
    setCurrentPage(1); // Reset to first page when search changes
  }, [searchTerm, colleges]);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  // Handle branch input
  const handleBranchChange = (e) => {
    setCurrentBranch(e.target.value);
  };

  // Add branch to the array
  const addBranch = () => {
    if (currentBranch.trim() && !formData.branch.includes(currentBranch.trim())) {
      setFormData({
        ...formData,
        branch: [...formData.branch, currentBranch.trim()]
      });
      setCurrentBranch('');
    }
  };

  // Remove branch from the array
  const removeBranch = (index) => {
    const updatedBranches = [...formData.branch];
    updatedBranches.splice(index, 1);
    setFormData({
      ...formData,
      branch: updatedBranches
    });
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    if (!formData.college_name.trim()) newErrors.college_name = 'College name is required';
    if (!formData.college_code.trim()) newErrors.college_code = 'College code is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsLoading(true);
    setApiError('');

    try {
      if (isEditing) {
        // Update existing college
        await axios.put(`${API_BASE_URL}/edit/${currentCollegeId}`, formData);
      } else {
        // Add new college
        await axios.post(`${API_BASE_URL}/add`, formData);
      }
      await fetchColleges(); // Refresh the list
      resetForm();
    } catch (error) {
      console.error('Error saving college:', error);
      setApiError('Failed to save college. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Edit college - fetch data and populate form
  const handleEdit = async (collegeId) => {
    setIsLoading(true);
    setApiError('');
    try {
      const response = await axios.get(`${API_BASE_URL}/${collegeId}`);
      const college = response.data?.college;
      setFormData({
        college_name: college.college_name,
        college_code: college.college_code,
        programme: college.programme || '',
        state: college.state || '',
        branch: [...college.branch],
        is_disabled: college.is_disabled
      });
      setIsEditing(true);
      setCurrentCollegeId(collegeId);
      document.getElementById('collegeForm').scrollIntoView();
    } catch (error) {
      console.error('Error fetching college:', error);
      setApiError('Failed to load college data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Toggle college disabled status
  const toggleDisabled = async (collegeId) => {
    setIsLoading(true);
    setApiError('');
    try {
      const college = colleges.find(c => c._id === collegeId);
      const updatedCollege = { ...college, is_disabled: !college.is_disabled };
      await axios.put(`${API_BASE_URL}/edit/${collegeId}`, updatedCollege);
      await fetchColleges(); // Refresh the list
    } catch (error) {
      console.error('Error updating college status:', error);
      setApiError('Failed to update college status. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Prepare for delete
  const confirmDelete = (college) => {
    setCollegeToDelete(college);
    setShowDeleteModal(true);
  };

  // Delete college
  const handleDelete = async (id) => {
    setIsLoading(true);
    setApiError('');
    try {
      await axios.delete(`${API_BASE_URL}/delete/${id}`);
      await fetchColleges(); // Refresh the list
      setShowDeleteModal(false);
      setCollegeToDelete(null);
    } catch (error) {
      console.error('Error deleting college:', error);
      setApiError('Failed to delete college. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      college_name: '',
      college_code: '',
      programme: '',
      state: '',
      branch: [],
      is_disabled: false
    });
    setCurrentBranch('');
    setIsEditing(false);
    setCurrentCollegeId(null);
    setErrors({});
  };

  // Pagination logic
  const totalPages = Math.ceil(filteredColleges.length / itemsPerPage);
  const paginatedColleges = filteredColleges.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  return (
    <div className="container mt-4">
      <h2 className="mb-4">College Management</h2>
      
      {/* Error message */}
      {apiError && (
        <div className="alert alert-danger mb-4">
          {apiError}
          <button 
            type="button" 
            className="btn-close float-end" 
            onClick={() => setApiError('')}
          ></button>
        </div>
      )}
      
      {/* Search and Add College Button */}
      <div className="row mb-4">
        <div className="col-md-6">
          <div className="input-group">
            <input
              type="text"
              className="form-control"
              placeholder="Search colleges..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <button className="btn btn-outline-secondary" type="button">
              <i className="bi bi-search"></i>
            </button>
          </div>
        </div>
        <div className="col-md-6 text-end">
          <button 
            className="btn btn-primary"
            onClick={() => {
              resetForm();
              document.getElementById('collegeForm').scrollIntoView();
            }}
            disabled={isLoading}
          >
            <i className="bi bi-plus-circle me-2"></i>Add College
          </button>
        </div>
      </div>
      
      {/* College Form */}
      <div className="card mb-4" id="collegeForm">
        <div className="card-header">
          <h5>{isEditing ? 'Edit College' : 'Add New College'}</h5>
        </div>
        <div className="card-body">
          <form onSubmit={handleSubmit}>
            <div className="row g-3">
              <div className="col-md-6">
                <label htmlFor="college_name" className="form-label">
                  College Name <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className={`form-control ${errors.college_name ? 'is-invalid' : ''}`}
                  id="college_name"
                  name="college_name"
                  value={formData.college_name}
                  onChange={handleInputChange}
                  disabled={isLoading}
                />
                {errors.college_name && (
                  <div className="invalid-feedback">{errors.college_name}</div>
                )}
              </div>
              
              <div className="col-md-6">
                <label htmlFor="college_code" className="form-label">
                  College Code <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className={`form-control ${errors.college_code ? 'is-invalid' : ''}`}
                  id="college_code"
                  name="college_code"
                  value={formData.college_code}
                  onChange={handleInputChange}
                  disabled={isLoading}
                />
                {errors.college_code && (
                  <div className="invalid-feedback">{errors.college_code}</div>
                )}
              </div>
              
              <div className="col-md-6">
                <label htmlFor="programme" className="form-label">Programme</label>
                <input
                  type="text"
                  className="form-control"
                  id="programme"
                  name="programme"
                  value={formData.programme}
                  onChange={handleInputChange}
                  disabled={isLoading}
                />
              </div>
              
              <div className="col-md-6">
                <label htmlFor="state" className="form-label">State</label>
                <input
                  type="text"
                  className="form-control"
                  id="state"
                  name="state"
                  value={formData.state}
                  onChange={handleInputChange}
                  disabled={isLoading}
                />
              </div>
              
              <div className="col-12">
                <label htmlFor="branch" className="form-label">Branches</label>
                <div className="input-group mb-2">
                  <input
                    type="text"
                    className="form-control"
                    id="branch"
                    value={currentBranch}
                    onChange={handleBranchChange}
                    placeholder="Add a branch"
                    disabled={isLoading}
                  />
                  <button
                    className="btn btn-outline-secondary"
                    type="button"
                    onClick={addBranch}
                    disabled={isLoading}
                  >
                    Add
                  </button>
                </div>
                
                {formData.branch.length > 0 && (
                  <div className="d-flex flex-wrap gap-2 mb-3">
                    {formData.branch.map((branch, index) => (
                      <span key={index} className="badge bg-primary">
                        {branch} &nbsp;
                        <button
                          type="button"
                          className="ms-2 btn-close btn-close-white"
                          aria-label="Remove"
                          onClick={() => removeBranch(index)}
                          style={{ fontSize: '0.5rem' }}
                          disabled={isLoading}
                        > X</button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
              
              <div className="col-md-6">
                <div className="form-check form-switch">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="is_disabled"
                    name="is_disabled"
                    checked={formData.is_disabled}
                    onChange={handleInputChange}
                    disabled={isLoading}
                  />
                  <label className="form-check-label" htmlFor="is_disabled">
                    Disabled
                  </label>
                </div>
              </div>
            </div>
            
            <div className="mt-4">
              <button 
                type="submit" 
                className="btn btn-primary me-2"
                disabled={isLoading}
              >
                {isLoading ? (
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                ) : isEditing ? (
                  'Update College'
                ) : (
                  'Add College'
                )}
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={resetForm}
                disabled={isLoading}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
      
      {/* Loading indicator */}
      {isLoading && (
        <div className="text-center my-4">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      )}
      
      {/* Colleges Table */}
      {!isLoading && (
        <div className="card">
          <div className="card-header">
            <h5>Colleges List</h5>
          </div>
          <div className="card-body">
            {filteredColleges.length === 0 ? (
              <div className="alert alert-info">No colleges found</div>
            ) : (
              <>
                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Code</th>
                        <th>Programme</th>
                        <th>State</th>
                        <th>Branches</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedColleges.map(college => (
                        <tr 
                          key={college.id} 
                          className={college.is_disabled ? 'table-secondary' : ''}
                        >
                          <td>{college.college_name}</td>
                          <td>{college.college_code}</td>
                          <td>{college.programme || '-'}</td>
                          <td>{college.state || '-'}</td>
                          <td>
                            {college.branch.length > 0 ? (
                              <span 
                                className="d-inline-block text-truncate" 
                                style={{ maxWidth: '150px' }}
                                title={college.branch.join(', ')}
                              >
                                {college.branch.join(', ')}
                              </span>
                            ) : '-'}
                          </td>
                          <td>
                            {college.is_disabled ? (
                              <span className="badge bg-danger">Disabled</span>
                            ) : (
                              <span className="badge bg-success">Active</span>
                            )}
                          </td>
                          <td>
                            <div className="d-flex gap-2">
                              <button
                                className="btn btn-sm btn-outline-primary"
                                onClick={() => handleEdit(college._id)}
                                disabled={isLoading}
                              >
                                <i className="bi bi-pencil"></i>
                              </button>
                              <button
                                className="btn btn-sm btn-outline-secondary"
                                onClick={() => toggleDisabled(college._id)}
                                disabled={isLoading}
                              >
                                {college.is_disabled ? (
                                  <i className="bi bi-toggle-off"></i>
                                ) : (
                                  <i className="bi bi-toggle-on"></i>
                                )}
                              </button>
                              <button
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => handleDelete(college._id)}
                                disabled={isLoading}
                              >
                                <i className="bi bi-trash"></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                {/* Pagination */}
                {totalPages > 1 && (
                  <nav className="mt-4">
                    <ul className="pagination justify-content-center">
                      <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <button 
                          className="page-link" 
                          onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                          disabled={isLoading}
                        >
                          Previous
                        </button>
                      </li>
                      
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                        <li 
                          key={page} 
                          className={`page-item ${currentPage === page ? 'active' : ''}`}
                        >
                          <button 
                            className="page-link" 
                            onClick={() => setCurrentPage(page)}
                            disabled={isLoading}
                          >
                            {page}
                          </button>
                        </li>
                      ))}
                      
                      <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                        <button 
                          className="page-link" 
                          onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                          disabled={isLoading}
                        >
                          Next
                        </button>
                      </li>
                    </ul>
                  </nav>
                )}
              </>
            )}
          </div>
        </div>
      )}
      
      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal fade show" style={{ display: 'block' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Confirm Delete</h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => setShowDeleteModal(false)}
                  disabled={isLoading}
                ></button>
              </div>
              <div className="modal-body">
                <p>Are you sure you want to delete <strong>{collegeToDelete?.college_name}</strong>?</p>
                <p>This action cannot be undone.</p>
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={() => setShowDeleteModal(false)}
                  disabled={isLoading}
                >
                  Cancel
                </button>
                <button 
                  type="button" 
                  className="btn btn-danger" 
                  onClick={handleDelete}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  ) : null}
                  Delete
                </button>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show"></div>
        </div>
      )}
    </div>
  );
};

export default CollegeManagement;